"use client"

import { useSession } from "next-auth/react"
import { useSyncExternalStore } from "react"
import type { Session } from "next-auth"
import { RAAUTH_ONLY } from "@/lib/auth-stack"
import {
   getSsoProbePhase,
   getSsoProbeServerPhase,
   subscribeSsoProbe,
} from "@/components/v2/ui/sso-probe-store"

/**
 * The single answer to "is this user logged in?" for the UI.
 *
 * Before this, the header, both navbars, the mobile menu and the SSO probe each called
 * `useSession()` and drew their own conclusions, and the header papered over the
 * disagreement with a 2.5s timer that kept the desktop navbar on a skeleton hoping the
 * probe would land — which the mobile navbar didn't do, so the two could disagree on
 * screen. Everything auth-shaped in the header now comes from here.
 *
 *   authenticated — a valid session exists. `auth()` on the server already destroys
 *                   sessions that aren't acceptable any more (revoked sid, dead raauth
 *                   grant) and clears their cookie, so a session reaching us is live; there
 *                   is no `session.error` to second-guess.
 *   anonymous     — no session, and no silent probe that could still produce one. Commit to
 *                   the logged-out UI. Correct-on-first-paint rather than a guess, because
 *                   the server seeds SessionProvider in app/layout.tsx.
 *   resolving     — we genuinely don't know yet: either NextAuth hasn't resolved, or this page
 *                   load's `prompt=none` probe is still outstanding, so the user may hold a
 *                   raauth session established on a sister site that we haven't asked about
 *                   yet (see lib/auth-cookies.ts). Show a skeleton. Bounded by the probe's
 *                   own give-up timer, not by a timer of ours.
 */
export type AuthState = "authenticated" | "anonymous" | "resolving"

export type AuthStateResult = {
   state: AuthState
   isAuthenticated: boolean
   /** True while the answer is still unknown — drives skeletons. */
   isResolving: boolean
   user: Session["user"] | undefined
   session: Session | null
}

export function useAuthState({
   /** Server-read: will this page load run a silent SSO probe that could still sign us in? */
   canAdoptSso = false,
}: { canAdoptSso?: boolean } = {}): AuthStateResult {
   const { data: session, status } = useSession()
   const probePhase = useSyncExternalStore(subscribeSsoProbe, getSsoProbePhase, getSsoProbeServerPhase)

   const user = session?.user ?? undefined

   // A probe can only change the outcome while we're logged out, RAAUTH_ONLY is on, and this
   // page load is actually going to ask (`canAdoptSso`, false only on the post-logout redirect
   // that carries `ra_sso_skip_probe`). `settled` means SilentSsoCheck has either finished or
   // decided not to ask — it ALWAYS reaches that phase, including on every early return, so
   // this can't hang.
   const probeCouldStillHelp = RAAUTH_ONLY && canAdoptSso && probePhase !== "settled"

   let state: AuthState
   if (user) state = "authenticated"
   else if (status === "loading" || probeCouldStillHelp) state = "resolving"
   else state = "anonymous"

   return {
      state,
      isAuthenticated: state === "authenticated",
      isResolving: state === "resolving",
      user,
      session: session ?? null,
   }
}
